import { z } from 'zod';

import { DomainError } from './errors.ts';

/**
 * The conversation is stored as **UIMessage** parts, not provider messages.
 *
 * A ModelMessage is what one provider wanted on one day: swapping models, or upgrading the SDK
 * across the major it breaks on, would leave a project's whole history in a shape nothing reads.
 * The UIMessage is the SDK's own durable shape and the one the chat UI renders directly, so the
 * conversion to provider messages happens per turn, from data that outlives the provider.
 */
export const MESSAGE_ROLES = ['user', 'assistant', 'system'] as const;
export const messageRoleSchema = z.enum(MESSAGE_ROLES);
export type MessageRole = z.infer<typeof messageRoleSchema>;

/**
 * The shape `parts` was written in. Parsed on read against the version the row carries rather
 * than against whatever the current code expects, so a new part shape adds an entry here instead
 * of replacing one — the same discipline artifact content follows, for the same reason: old rows
 * must keep rendering.
 */
export const MESSAGE_SCHEMA_VERSION = 1;

/**
 * A ceiling on one message. Parts are jsonb in a table the conversation reads in full every turn,
 * and a runaway tool loop can emit parts without bound, so the write refuses rather than making
 * every later turn pay to read it back.
 */
export const MAX_MESSAGE_PARTS = 500;

/**
 * The structural floor, and deliberately no more than that.
 *
 * Every UIMessage part is an object with a `type`; beyond that the shapes belong to the AI SDK and
 * to whichever tools a later ticket adds, and `validateUIMessages()` in the server is what checks
 * them properly — it knows the tool set, which this package must not. Restating those shapes here
 * would be a second definition to keep in step, and the one that drifted would reject history the
 * SDK is perfectly happy with.
 */
const messagePartSchema = z.looseObject({ type: z.string().min(1) });

const messagePartsSchema = z.array(messagePartSchema).min(1).max(MAX_MESSAGE_PARTS);

const PARTS_SCHEMA_BY_VERSION: Record<number, typeof messagePartsSchema> = {
  [MESSAGE_SCHEMA_VERSION]: messagePartsSchema,
};

/** Validate on read: a stored row outlives the code that wrote it. */
export const parseMessageParts = (schemaVersion: number, value: unknown): unknown[] => {
  const schema = PARTS_SCHEMA_BY_VERSION[schemaVersion];
  if (schema === undefined) {
    throw new DomainError(
      'unknown_schema_version',
      `no message part schema for version ${String(schemaVersion)}`,
      { schema_version: schemaVersion },
    );
  }

  const parsed = schema.safeParse(value);
  if (!parsed.success) {
    // Always a read of something already stored, so the fault is ours rather than a caller's.
    throw new DomainError(
      'stored_content_invalid',
      'stored message parts do not match the schema',
      {
        schema_version: schemaVersion,
        issues: z.prettifyError(parsed.error),
      },
    );
  }

  return parsed.data;
};

/** What a client may send as a new user message; the id is the server's to mint. */
export const userMessageInputSchema = z.object({
  text: z.string().min(1).max(20_000),
});
export type UserMessageInput = z.infer<typeof userMessageInputSchema>;

/**
 * The `message` turn event: the server-generated id of a message that has just been persisted.
 * The client needs it to reconcile what it optimistically rendered with what will come back on a
 * reload, and it is the only way an id minted inside the worker reaches the browser.
 */
export const messageEventPayloadSchema = z.object({
  message_id: z.uuid(),
  role: messageRoleSchema,
});
export type MessageEventPayload = z.infer<typeof messageEventPayloadSchema>;
